<div class="segments-table-container">
  <div class="segments-table-toolbar" *ngIf="originalSegmentCount > 0 && !loading">
    <button
      type="button"
      class="status-filter-trigger"
      (click)="toggleStatusFilter($event)"
      [attr.aria-expanded]="isStatusFilterOpen"
    >
      <span>Status</span>
      <span *ngIf="hasStatusFilterValues" class="status-filter-badge">{{
        selectedStatuses.length
      }}</span>
    </button>
  </div>
  <!-- Loading State -->
  <ng-container *ngIf="loading">
    <div class="table-scroll-wrapper" #tableScrollWrapper>
      <div class="table-skeleton">
        <div class="table-skeleton-header">
          <div class="th" style="min-width: 250px">Name</div>
          <div class="th" style="min-width: 140px">Status</div>
          <div class="th" style="min-width: 140px">Type</div>
          <div class="th" style="min-width: 150px">Created On</div>
          <div class="th" style="min-width: 160px">Total Users</div>
          <div class="th" style="min-width: 160px">Active Users</div>
          <div class="th" style="min-width: 160px">Invalid Users</div>
          <div class="th" style="min-width: 184px">Unsubscribed Users</div>
          <div class="th" style="min-width: 100px">Actions</div>
        </div>
        <div class="table-skeleton-body">
          <div class="tr" *ngFor="let _ of skeletonRows; trackBy: trackByIndex">
            <div
              class="td"
              style="min-width: 250px; display: flex; flex-direction: column; gap: 6px"
            >
              <div class="shimmer-line w-180"></div>
              <div class="shimmer-line w-140"></div>
            </div>
            <div class="td" style="min-width: 140px">
              <div class="shimmer-pill w-80"></div>
            </div>
            <div class="td" style="min-width: 140px">
              <div class="shimmer-pill w-80"></div>
            </div>
            <div class="td" style="min-width: 150px">
              <div class="shimmer-line w-120"></div>
            </div>
            <div class="td" style="min-width: 160px">
              <div class="shimmer-line w-100"></div>
            </div>
            <div class="td" style="min-width: 160px">
              <div class="shimmer-line w-100"></div>
            </div>
            <div class="td" style="min-width: 160px">
              <div class="shimmer-line w-100"></div>
            </div>
            <div class="td" style="min-width: 184px">
              <div class="shimmer-line w-100"></div>
            </div>
            <div class="td" style="min-width: 100px; display: flex; gap: 20px">
              <div class="shimmer-circle small"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ng-container>

  <!-- Not Loading State -->
  <ng-container *ngIf="!loading">
    <!-- Table with Data -->
    <ng-container *ngIf="filteredSegments.length > 0 || originalSegmentCount > 0">
      <div class="table-scroll-wrapper" #tableScrollWrapper>
        <table
          mat-table
          [dataSource]="dataSource"
          matSort
          (matSortChange)="onMatSortChange($event)"
          class="mat-mdc-table"
          data-testid="segments-table"
        >
          <!-- Name -->
          <ng-container matColumnDef="name">
            <th mat-header-cell *matHeaderCellDef mat-sort-header style="min-width: 250px">
              Name
              <img [src]="getSortIcon('name')" alt="sort" class="sort-icon" />
            </th>
            <td mat-cell *matCellDef="let segment">
              <div class="segment-name-cell">
                <span
                  class="segment-name p3 text-semibold clickable"
                  (click)="onViewSegmentDetails(segment)"
                  >{{ segment.name }}</span
                >
                <span class="created-by p7">{{ segment.description }}</span>
              </div>
            </td>
          </ng-container>

          <!-- Status -->
          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef style="min-width: 140px">Status</th>
            <td mat-cell *matCellDef="let segment">
              <span
                [ngClass]="{
                  'status-active': segment.segment_status === true,
                  'status-stopped': segment.segment_status === false,
                }"
              >
                {{ getStatusDisplay(segment.segment_status) }}
              </span>
            </td>
          </ng-container>

          <!-- Type -->
          <ng-container matColumnDef="source_type">
            <th mat-header-cell *matHeaderCellDef style="min-width: 140px">Type</th>
            <td mat-cell *matCellDef="let segment">
              <span
                [ngClass]="{
                  'type-upload': segment.source_type === 'upload',
                  'type-rule': segment.source_type !== 'upload',
                }"
              >
                {{ getSourceTypeDisplay(segment.source_type) }}
              </span>
            </td>
          </ng-container>

          <!-- Created On -->
          <ng-container matColumnDef="created_at">
            <th mat-header-cell *matHeaderCellDef style="min-width: 150px">Created On</th>
            <td mat-cell *matCellDef="let segment">
              <div class="created-on-cell p5">
                {{ formatCreatedDate(segment.created_at) }}
              </div>
            </td>
          </ng-container>

          <!-- Total Users -->
          <ng-container matColumnDef="total_customers">
            <th mat-header-cell *matHeaderCellDef style="min-width: 160px">Total Users</th>
            <td mat-cell *matCellDef="let segment">
              <div class="total-users-cell p5">
                {{ segment.total_customers }}
              </div>
            </td>
          </ng-container>

          <!-- Active Users -->
          <ng-container matColumnDef="active_customers">
            <th mat-header-cell *matHeaderCellDef style="min-width: 160px">Active Users</th>
            <td mat-cell *matCellDef="let segment">
              <div class="active-users-cell p5">
                {{ segment.active_customers }}
              </div>
            </td>
          </ng-container>

          <!-- Invalid Users -->
          <ng-container matColumnDef="inactive_customers">
            <th mat-header-cell *matHeaderCellDef style="min-width: 160px">Invalid Users</th>
            <td mat-cell *matCellDef="let segment">
              <div class="invalid-users-cell p5">
                {{ segment.inactive_customers }}
              </div>
            </td>
          </ng-container>

          <!-- Unsubscribed Users -->
          <ng-container matColumnDef="unsubscribed_customers">
            <th mat-header-cell *matHeaderCellDef style="min-width: 184px">Unsubscribed Users</th>
            <td mat-cell *matCellDef="let segment">
              <div class="unsubscribed-users-cell p5">
                {{ segment.unsubscribed_customers }}
              </div>
            </td>
          </ng-container>

          <!-- Actions -->
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef style="min-width: 100px">Actions</th>
            <td mat-cell *matCellDef="let segment">
              <div class="action-buttons">
                <button
                  type="button"
                  class="action-btn menu-btn"
                  [attr.data-testid]="'segment-actions-menu-button-' + segment.id"
                  [matMenuTriggerFor]="actionMenu"
                  (click)="showActionMenu($event, segment)"
                  aria-label="Segment actions"
                >
                  <svg
                    width="16"
                    height="17"
                    viewBox="0 0 16 17"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.33203 8.50008C7.33203 8.67689 7.40227 8.84646 7.52729 8.97149C7.65232 9.09651 7.82189 9.16675 7.9987 9.16675C8.17551 9.16675 8.34508 9.09651 8.4701 8.97149C8.59513 8.84646 8.66536 8.67689 8.66536 8.50008C8.66536 8.32327 8.59513 8.1537 8.4701 8.02868C8.34508 7.90365 8.17551 7.83341 7.9987 7.83341C7.82189 7.83341 7.65232 7.90365 7.52729 8.02868C7.40227 8.1537 7.33203 8.32327 7.33203 8.50008Z"
                      stroke="#15172E"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M7.33203 13.1667C7.33203 13.3436 7.40227 13.5131 7.52729 13.6382C7.65232 13.7632 7.82189 13.8334 7.9987 13.8334C8.17551 13.8334 8.34508 13.7632 8.4701 13.6382C8.59513 13.5131 8.66536 13.3436 8.66536 13.1667C8.66536 12.9899 8.59513 12.8204 8.4701 12.6953C8.34508 12.5703 8.17551 12.5001 7.9987 12.5001C7.82189 12.5001 7.65232 12.5703 7.52729 12.6953C7.40227 12.8204 7.33203 12.9899 7.33203 13.1667Z"
                      stroke="#15172E"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M7.33203 3.83341C7.33203 4.01023 7.40227 4.17979 7.52729 4.30482C7.65232 4.42984 7.82189 4.50008 7.9987 4.50008C8.17551 4.50008 8.34508 4.42984 8.4701 4.30482C8.59513 4.17979 8.66536 4.01023 8.66536 3.83341C8.66536 3.6566 8.59513 3.48703 8.4701 3.36201C8.34508 3.23699 8.17551 3.16675 7.9987 3.16675C7.82189 3.16675 7.65232 3.23699 7.52729 3.36201C7.40227 3.48703 7.33203 3.6566 7.33203 3.83341Z"
                      stroke="#15172E"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr
            mat-row
            *matRowDef="let row; columns: displayedColumns"
            [attr.data-testid]="'segments-table-row-' + row.id"
          ></tr>
        </table>
      </div>
      <app-empty-state
        testId="segments-no-results-empty-state"
        *ngIf="filteredSegments.length === 0 && originalSegmentCount > 0"
        image="assets/images/package/Illustrations.svg"
        imageAlt="No Segments"
        [title]="emptyStateConfig.title"
        [description]="emptyStateConfig.description"
        actionLabel="Add Segment"
        [showAction]="emptyStateConfig.showAddButton"
        (action)="onAddSegment()"
      >
      </app-empty-state>
      <!-- Pagination -->
      <div class="pagination-container" *ngIf="filteredSegments.length > 0">
        <!-- Left: Navigation Controls -->
        <div class="pagination-navigation">
          <button
            type="button"
            class="paginator-btn"
            aria-label="First page"
            [disabled]="isFirstPage"
            (click)="goToPage(0)"
          >
            &laquo;
          </button>
          <button
            type="button"
            class="paginator-btn"
            aria-label="Previous page"
            [disabled]="isFirstPage"
            (click)="goToPage(currentPage - 1)"
          >
            &lsaquo;
          </button>
          <span class="paginator-page-label p5">
            Page {{ currentPage + 1 }} of {{ totalPages }}
          </span>
          <button
            type="button"
            class="paginator-btn"
            aria-label="Next page"
            [disabled]="isLastPage"
            (click)="goToPage(currentPage + 1)"
          >
            &rsaquo;
          </button>
          <button
            type="button"
            class="paginator-btn"
            aria-label="Last page"
            [disabled]="isLastPage"
            (click)="goToPage(totalPages - 1)"
          >
            &raquo;
          </button>
        </div>

        <!-- Center: Items per page dropdown -->
        <div class="pagination-center">
          <div class="items-per-page">
            <span class="items-per-page-label">Items per page</span>
            <mat-form-field appearance="outline" class="items-per-page-field">
              <mat-select
                [(ngModel)]="rowsPerPage"
                (selectionChange)="onRowsPerPageChange($event)"
                panelClass="items-per-page-panel"
                aria-label="Items per page"
              >
                <mat-option
                  *ngFor="let option of rowsPerPageOptions; trackBy: trackByValue"
                  [value]="option.value"
                >
                  {{ option.label }}
                </mat-option>
              </mat-select>
            </mat-form-field>
          </div>
        </div>

        <!-- Right: Current page report -->
        <div class="pagination-info">
          <span class="current-page-report">
            Items {{ currentPage * rowsPerPage + 1 }} - {{ getEndItem() }} of {{ totalRecords }}
          </span>
        </div>
      </div>
    </ng-container>

    <!-- Empty State for Search/Filter Results -->

    <!-- Empty State for Initial No Data -->
    <app-empty-state
      testId="segments-empty-state"
      *ngIf="originalSegmentCount === 0 && filteredSegments.length === 0"
      image="assets/images/package/Illustrations.svg"
      imageAlt="No Segments"
      [title]="emptyStateConfig.title"
      [description]="emptyStateConfig.description"
      actionLabel="Add Segment"
      [showAction]="emptyStateConfig.showAddButton"
      (action)="onAddSegment()"
    >
    </app-empty-state>
  </ng-container>
</div>

<!-- Segment Action Menu -->
<mat-menu #actionMenu="matMenu" class="action-menu-panel">
  <button
    type="button"
    mat-menu-item
    class="action-menu-item"
    data-testid="segment-view-menu-item"
    (click)="onViewSegment()"
  >
    <span>View</span>
    <svg width="10" height="12" viewBox="0 0 10 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M6.16797 0.75V3.08333C6.16797 3.23804 6.22943 3.38642 6.33882 3.49581C6.44822 3.60521 6.59659 3.66667 6.7513 3.66667H9.08464M6.16797 0.75H2.08464C1.77522 0.75 1.47847 0.872916 1.25968 1.09171C1.04089 1.3105 0.917969 1.60725 0.917969 1.91667V10.0833C0.917969 10.3928 1.04089 10.6895 1.25968 10.9083C1.47847 11.1271 1.77522 11.25 2.08464 11.25H7.91797C8.22739 11.25 8.52413 11.1271 8.74293 10.9083C8.96172 10.6895 9.08464 10.3928 9.08464 10.0833V3.66667M6.16797 0.75L9.08464 3.66667M3.2513 8.91667H6.7513M3.2513 6.58333H6.7513"
        stroke="#555963"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  </button>

  <button
    type="button"
    mat-menu-item
    class="action-menu-item archive-item"
    data-testid="segment-delete-menu-item"
    (click)="onDeleteSegment()"
  >
    <span>Delete</span>
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M1.33203 3.08333H10.6654M4.83203 5.41667V8.91667M7.16536 5.41667V8.91667M1.91536 3.08333L2.4987 10.0833C2.4987 10.3928 2.62161 10.6895 2.84041 10.9083C3.0592 11.1271 3.35595 11.25 3.66536 11.25H8.33203C8.64145 11.25 8.9382 11.1271 9.15699 10.9083C9.37578 10.6895 9.4987 10.3928 9.4987 10.0833L10.082 3.08333M4.2487 3.08333V1.33333C4.2487 1.17862 4.31016 1.03025 4.41955 0.920854C4.52895 0.811458 4.67732 0.75 4.83203 0.75H7.16536C7.32007 0.75 7.46845 0.811458 7.57784 0.920854C7.68724 1.03025 7.7487 1.17862 7.7487 1.33333V3.08333"
        stroke="#DA1E28"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  </button>
</mat-menu>

<!-- Status Filter Panel -->
<div class="status-filter-panel" *ngIf="isStatusFilterOpen">
  <div class="status-filter-content">
    <div class="filter-options">
      <div *ngFor="let option of statusOptions; trackBy: trackByValue" class="p-field-checkbox">
        <mat-checkbox
          [id]="option.value"
          [checked]="isStatusSelected(option.value)"
          (change)="onStatusToggle(option.value, $event.checked)"
        ></mat-checkbox>
        <label [for]="option.value" class="p5">{{ option.label }}</label>
      </div>
    </div>
    <div class="filter-actions">
      <button
        type="button"
        style="padding-left: 0px"
        class="btn btn-tertiary btn-small"
        [disabled]="!hasDraftStatusFilterValues"
        (click)="clearStatusFilter()"
        [ngClass]="{ disabled: !hasDraftStatusFilterValues }"
      >
        Clear
      </button>
      <div class="right-buttons">
        <button type="button" class="btn btn-secondary btn-small" (click)="cancelStatusFilter()">
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-primary btn-small"
          (click)="applyStatusFilter()"
          [disabled]="!hasDraftStatusFilterChanges"
          [ngClass]="{ disabled: !hasDraftStatusFilterChanges }"
        >
          Apply
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Segment Details Modal -->
<app-segment-details-modal
  [visible]="showSegmentDetailsModal"
  [segment]="selectedSegment"
  (close)="onCloseSegmentDetailsModal()"
  (editSegment)="onEditSegment()"
  (deleteSegment)="onDeleteSegment()"
>
</app-segment-details-modal>

<!-- Delete Segment Modal -->
<app-delete-segment-modal
  [visible]="showDeleteModal"
  [segment]="segmentToDelete"
  [loading]="isDeletingSegment"
  (close)="onCloseDeleteModal()"
  (confirm)="onConfirmDelete($event)"
>
</app-delete-segment-modal>
